import { Snackbar, Paper, Box, Typography, IconButton as MuiIconButton } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useNotifications } from "src/contexts/NotificationContext";

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  referenceType: string;
  referenceId: number;
  createdAt: string;
}

interface ToastNotification extends NotificationItem {
  toastId: string;
}

const GlobalToastNotification = () => {
  const { notifications } = useNotifications();
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  
  const prevNotificationsRef = useRef<NotificationItem[]>([]);

  const getBackgroundColor = (referenceType: string) => {
    const type = referenceType?.toLowerCase();
    
    switch (type) {
      case 'booking':
        return 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)';
      case 'customercommunication':
        return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
      case 'review':
        return 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
      default:
        return 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)';
    }
  };

  // Track new notifications and add to toast stack
  useEffect(() => {
    if (notifications.length > 0 && prevNotificationsRef.current.length > 0) {
      const latestNotification = notifications[0];
      const isNew = !prevNotificationsRef.current.some(n => n.id === latestNotification.id);
      if (isNew) {
        // Add new toast to the stack with unique ID
        const newToast: ToastNotification = {
          ...latestNotification,
          toastId: `${latestNotification.id}-${Date.now()}`
        };
        
        setToasts(prev => [newToast, ...prev]);
        
        // Auto remove after 7 seconds
        setTimeout(() => {
          setToasts(prev => prev.filter(toast => toast.toastId !== newToast.toastId));
        }, 7000);
      }
    }
    
    // Update previous notifications reference
    prevNotificationsRef.current = [...notifications];
  }, [notifications]);

  const handleClose = (toastId: string) => {
    setToasts(prev => prev.filter(toast => toast.toastId !== toastId));
  };

  // Don't show anything if no toasts
  if (toasts.length === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column-reverse",
        gap: 1.5,
        maxHeight: "calc(100vh - 100px)",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          background: "rgba(0,0,0,0.1)",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(0,0,0,0.3)",
          borderRadius: "4px",
        },
      }}
    >
      {toasts.map((toast, index) => {
        const bgColor = getBackgroundColor(toast.referenceType);
        
        return (
          <Paper
            key={toast.toastId}
            elevation={6}
            sx={{
              p: 2.5,
              minWidth: 320,
              maxWidth: 380,
              background: bgColor,
              borderRadius: 2,
              position: "relative",
              transition: "all 0.3s ease",
              animation: `slideIn 0.3s ease-out ${index * 0.05}s both`,
              border: "1px solid rgba(0,0,0,0.1)",
              opacity: 0,
              transform: "translateX(100%)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 8
              },
              "@keyframes slideIn": {
                from: {
                  transform: "translateX(100%)",
                  opacity: 0
                },
                to: {
                  transform: "translateX(0)",
                  opacity: 1
                }
              }
            }}
          >
            <MuiIconButton
              size="small"
              onClick={() => handleClose(toast.toastId)}
              sx={{ 
                position: "absolute", 
                right: 8, 
                top: 8,
                padding: 0.5,
                color: "#666",
                zIndex: 1,
                "&:hover": {
                  color: "#000",
                  backgroundColor: "rgba(0,0,0,0.05)"
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </MuiIconButton>

            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "#be10ee",
                  animation: "pulse 1.5s ease-in-out infinite",
                  display: "inline-block",
                  mt: 1,
                  flexShrink: 0,
                  "@keyframes pulse": {
                    "0%": { transform: "scale(1)", opacity: 1 },
                    "50%": { transform: "scale(1.5)", opacity: 0.7 },
                    "100%": { transform: "scale(1)", opacity: 1 }
                  }
                }}
              />
              <Box flex={1}>
                <Typography 
                  sx={{ 
                    fontWeight: "bold", 
                    fontSize: "0.9rem",
                    color: "#000000",
                    mb: 0.5,
                    pr: 2
                  }}
                >
                  {toast.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#333333",
                    fontSize: "0.875rem",
                    lineHeight: 1.5
                  }}
                >
                  {toast.message}
                </Typography>
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default GlobalToastNotification;